import { useState, useEffect, useCallback } from 'react';
import { format, addMinutes, isBefore } from 'date-fns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { supabase } from '../lib/supabase';
import { uploadImageToStorage, uploadMediaToStorage, deleteImageFromStorage } from '../lib/fileUpload';
import { trimAllSegments, type TrimProgress } from '../lib/videoTrimmer';
import { generateCtaCardImage } from '../lib/generateCtaCard';
import toast from 'react-hot-toast';
import type { ContentType, StoryPartData, ReelsStoryTaskFormProps } from '../components/task/types';
import {
  autoSplitContentForStory,
  parseAIStoryResponse,
  detectSlideTemplate,
  STORY_SLIDE_DEFAULT_DURATION,
  STORY_MAX_SLIDES,
  STORY_MAX_DURATION_SECONDS,
  REELS_MAX_CAPTION_LENGTH,
} from '../components/task/ReelsStoryConstants';

const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Warsaw';
  } catch {
    return 'Europe/Warsaw';
  }
};

export function useReelsStoryTaskForm({ onClose, onSubmit, editingTask, defaultContentType }: ReelsStoryTaskFormProps) {
  // ─── Content Type ──────────────────────────────────────────────────
  const [contentType, setContentType] = useState<ContentType>(
    editingTask?.content_type || defaultContentType || 'reel'
  );

  // ─── Core Fields ──────────────────────────────────────────────────
  const [content, setContent] = useState(editingTask?.content || '');
  const [selectedPlatform, setSelectedPlatform] = useState(editingTask?.platform || 'facebook');
  const [selectedAccount, setSelectedAccount] = useState<string | null>(editingTask?.account_id || null);
  const [selectedDate, setSelectedDate] = useState(
    editingTask ? new Date(editingTask.scheduled_for) : new Date()
  );
  const [selectedTime, setSelectedTime] = useState(
    editingTask ? format(new Date(editingTask.scheduled_for), 'HH:mm') : format(addMinutes(new Date(), 10), 'HH:mm')
  );
  const [selectedTimezone, setSelectedTimezone] = useState(
    editingTask?.timezone || detectBrowserTimezone()
  );
  const [userDefaultTimezone, setUserDefaultTimezone] = useState<string | null>(null);
  const [cycle, setCycle] = useState(editingTask?.cycle || 'once');
  const [selectedDays, setSelectedDays] = useState<string[]>(editingTask?.cycle_days || []);
  const [customWebhook, setCustomWebhook] = useState(
    editingTask?.webhook_url === import.meta.env.VITE_DEFAULT_WEBHOOK ? '' : (editingTask?.webhook_url || '')
  );

  // ─── Reels Fields ─────────────────────────────────────────────────
  const [reelMode, setReelMode] = useState<'upload' | 'ai_generate'>(
    editingTask?.video_url ? 'upload' : (editingTask?.metadata?.reel_mode || 'upload')
  );
  const [storyMode, setStoryMode] = useState<'upload' | 'ai_generate'>(
    editingTask?.metadata?.story_mode || 'upload'
  );
  const [videoUrl, setVideoUrl] = useState<string | null>(editingTask?.video_url || null);
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(editingTask?.cover_image_url || null);
  const [reelCtaUrl, setReelCtaUrl] = useState<string>(editingTask?.metadata?.cta_url || '');
  const [reelCtaButtonText, setReelCtaButtonText] = useState<string>(editingTask?.metadata?.cta_button_text || '');
  const [referenceImages, setReferenceImages] = useState<string[]>(
    editingTask?.metadata?.reference_images || []
  );
  const [logoUrl, setLogoUrl] = useState<string | null>(
    editingTask?.metadata?.logo_url || null
  );

  // ─── Stories Fields ───────────────────────────────────────────────
  const [storyParts, setStoryParts] = useState<StoryPartData[]>(
    editingTask?.story_parts || []
  );

  // ─── Shared State ─────────────────────────────────────────────────
  const [isAiGenerated, setIsAiGenerated] = useState(
    editingTask?.metadata?.is_ai_generated !== undefined ? editingTask.metadata.is_ai_generated : true
  );
  const [connectedAccounts, setConnectedAccounts] = useState<Array<{
    id: string;
    name: string;
    platform: string;
    account_type: string;
    status?: string;
  }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isVideoSplitting, setIsVideoSplitting] = useState(false);
  const [minTime, setMinTime] = useState(getMinTime());
  const [showWebhookInfo, setShowWebhookInfo] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);

  // ─── Effects ──────────────────────────────────────────────────────
  useEffect(() => {
    loadConnectedAccountsForReelsStory();
    loadUserDefaultTimezoneForReelsStory();
    const interval = setInterval(() => setMinTime(getMinTime()), 60000);
    return () => clearInterval(interval);
  }, [selectedPlatform]);

  // ─── Load accounts (only platforms supporting reels/stories) ──────
  const loadConnectedAccountsForReelsStory = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('connected_accounts')
        .select('*')
        .eq('platform', selectedPlatform)
        .eq('user_id', user.id);

      if (error) throw error;

      const transformedData = (data || []).map(account => ({
        id: account.id,
        name: account.name || 'Unnamed Account',
        platform: account.platform,
        account_type: account.account_type || account.platform,
        status: account.status
      }));

      transformedData.sort((a, b) => a.name.localeCompare(b.name));
      setConnectedAccounts(transformedData);
    } catch (error) {
      console.error('Error loading accounts for Reels/Story:', error);
      toast.error('Failed to load connected accounts');
    }
  };

  const loadUserDefaultTimezoneForReelsStory = useCallback(async () => {
    if (editingTask?.timezone) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('default_timezone')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading user timezone:', error);
        return;
      }

      if (data?.default_timezone) {
        setUserDefaultTimezone(data.default_timezone);
        setSelectedTimezone(data.default_timezone);
      }
    } catch (error) {
      console.error('Error loading user default timezone:', error);
    }
  }, [editingTask?.timezone]);

  // ─── Helpers ──────────────────────────────────────────────────────
  function getMinTime(): string {
    const now = new Date();
    const zonedNow = zonedTimeToUtc(now, selectedTimezone);
    const minDateTime = addMinutes(zonedNow, 10);
    return format(minDateTime, 'HH:mm');
  }

  function getCurrentTimeInTimezone(): string {
    const now = new Date();
    const zonedNow = zonedTimeToUtc(now, selectedTimezone);
    return format(zonedNow, 'HH:mm');
  }

  const autoCorrectDateTimeForReelsStory = (): { date: Date; time: string } => {
    const now = new Date();
    const zonedNow = zonedTimeToUtc(now, selectedTimezone);
    const selectedDateTime = zonedTimeToUtc(
      new Date(`${format(selectedDate, 'yyyy-MM-dd')}T${selectedTime}`),
      selectedTimezone
    );
    const minDateTime = addMinutes(zonedNow, 10);

    if (isBefore(selectedDateTime, minDateTime)) {
      const correctedDate = minDateTime;
      const correctedTime = format(correctedDate, 'HH:mm');
      toast.success('Time automatically adjusted to +10 minutes from now', { duration: 3000, icon: '⏰' });
      return { date: correctedDate, time: correctedTime };
    }

    return { date: selectedDate, time: selectedTime };
  };

  // ─── Content Type Handler ─────────────────────────────────────────
  const handleContentTypeSelectForReelsStory = (type: ContentType) => {
    setContentType(type);
  };

  // ─── Platform & Account ───────────────────────────────────────────
  const handlePlatformSelectForReelsStory = (platform: string) => {
    setSelectedPlatform(platform);
    setSelectedAccount(null);
  };

  const handleAccountSelectForReelsStory = (accountId: string) => {
    setSelectedAccount(accountId);
  };

  // ─── Reels Video Handlers ────────────────────────────────────────
  const handleReelsVideoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      toast.loading('Przesyłanie wideo...');

      const { publicUrl, error } = await uploadMediaToStorage(file, { maxSizeMB: 100 });
      if (error) throw error;
      if (!publicUrl) throw new Error('Failed to get public URL');

      setVideoUrl(publicUrl);
      toast.dismiss();
      toast.success('Wideo przesłane pomyślnie');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : 'Failed to upload video');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleReelsVideoRemove = async () => {
    try {
      setIsUploading(true);
      if (videoUrl) {
        await deleteImageFromStorage(videoUrl);
      }
      setVideoUrl(null);
      toast.success('Wideo usunięte');
    } catch (error) {
      console.error('Error removing video:', error);
      toast.error('Failed to remove video');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReelsCoverImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      toast.loading('Przesyłanie okładki...');

      const { publicUrl, error } = await uploadImageToStorage(file);
      if (error) throw error;
      if (!publicUrl) throw new Error('Failed to get public URL');

      setCoverImageUrl(publicUrl);
      toast.dismiss();
      toast.success('Okładka przesłana');
    } catch (error) {
      console.error('Error uploading cover:', error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : 'Failed to upload cover image');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleReelsCoverImageRemove = async () => {
    try {
      setIsUploading(true);
      if (coverImageUrl) {
        await deleteImageFromStorage(coverImageUrl);
      }
      setCoverImageUrl(null);
      toast.success('Okładka usunięta');
    } catch (error) {
      console.error('Error removing cover:', error);
      toast.error('Failed to remove cover image');
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Story Parts Handlers ────────────────────────────────────────
  const handleAddStoryPart = () => {
    if (storyParts.length >= STORY_MAX_SLIDES) {
      toast.error(`Maximum ${STORY_MAX_SLIDES} slides allowed`);
      return;
    }

    const newPart: StoryPartData = {
      partOrder: storyParts.length + 1,
      content: '',
      mediaUrl: null,
      mediaType: 'image',
      durationSeconds: STORY_SLIDE_DEFAULT_DURATION,
    };
    setStoryParts([...storyParts, newPart]);
  };

  const handleRemoveStoryPart = (index: number) => {
    const updated = storyParts.filter((_, i) => i !== index)
      .map((part, i) => ({ ...part, partOrder: i + 1 }));
    setStoryParts(updated);
  };

  const handleUpdateStoryPart = (index: number, updates: Partial<StoryPartData>) => {
    const updated = [...storyParts];
    updated[index] = { ...updated[index], ...updates };
    setStoryParts(updated);
  };

  const handleMoveStoryPart = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= storyParts.length) return;
    const updated = [...storyParts];
    const [movedItem] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, movedItem);
    setStoryParts(updated.map((part, i) => ({ ...part, partOrder: i + 1 })));
  };

  const handleAutoSplitContentForStory = () => {
    if (!content.trim()) {
      toast.error('Wpisz tekst, aby go podzielić na slajdy');
      return;
    }

    // Try AI-format parse first (--- with template tags)
    const aiParsed = parseAIStoryResponse(content);
    if (aiParsed && aiParsed.length > 1) {
      setStoryParts(aiParsed);
      toast.success(`Tekst podzielony na ${aiParsed.length} slajdów (rozpoznano format AI)`);
      return;
    }

    // Fallback to character-based split
    const segments = autoSplitContentForStory(content);
    const newParts: StoryPartData[] = segments.map((text, i) => ({
      partOrder: i + 1,
      content: text,
      mediaUrl: null,
      mediaType: 'image' as const,
      durationSeconds: STORY_SLIDE_DEFAULT_DURATION,
      template: detectSlideTemplate(text, i, segments.length),
    }));

    setStoryParts(newParts);
    toast.success(`Tekst podzielony na ${newParts.length} slajdów`);
  };

  const handleStoryPartMediaChange = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      toast.loading(`Przesyłanie media slajdu ${index + 1}...`);

      const { publicUrl, error } = await uploadMediaToStorage(file, { maxSizeMB: 100 });
      if (error) throw error;
      if (!publicUrl) throw new Error('Failed to get public URL');

      const isVideo = file.type.startsWith('video/');
      handleUpdateStoryPart(index, {
        mediaUrl: publicUrl,
        mediaType: isVideo ? 'video' : 'image',
      });

      toast.dismiss();
      toast.success('Media przesłane');
    } catch (error) {
      console.error('Error uploading story part media:', error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : 'Failed to upload media');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  // ─── Video Auto-Split (Client-Side) ────────────────────────────────
  // State: holds info about uploaded video for splitting
  const [videoSplitInfo, setVideoSplitInfo] = useState<{
    videoUrl: string;
    totalDuration: number; // total seconds
    segmentDuration: number; // desired segment length in seconds
    fileName: string;
    originalFile: File; // original file for direct FFmpeg access (avoids re-download)
  } | null>(null);

  // Step 1: User uploads a video → detect duration → show config
  const handleVideoAutoSplit = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Proszę wybrać plik wideo (MP4, MOV)');
      return;
    }

    try {
      setIsVideoSplitting(true);
      toast.loading('Przesyłanie wideo...');

      // 1. Upload the video
      const { publicUrl, error: uploadError } = await uploadMediaToStorage(file, { maxSizeMB: 500 });
      if (uploadError) throw uploadError;
      if (!publicUrl) throw new Error('Nie udało się uzyskać URL wideo');

      toast.dismiss();
      toast.loading('Odczytywanie długości wideo...');

      // 2. Read duration from <video> element
      const duration = await new Promise<number>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          resolve(Math.ceil(video.duration));
          video.remove();
        };
        video.onerror = () => {
          reject(new Error('Nie udało się odczytać metadanych wideo'));
          video.remove();
        };
        video.src = publicUrl;
      });

      toast.dismiss();

      if (duration <= STORY_MAX_DURATION_SECONDS) {
        // Video is short enough for a single slide — add directly
        const newParts: StoryPartData[] = [{
          partOrder: storyParts.length + 1,
          content: '',
          mediaUrl: publicUrl,
          mediaType: 'video',
          durationSeconds: duration,
        }];
        setStoryParts(prev => [...prev, ...newParts]);
        toast.success(`Wideo (${duration}s) dodane jako jeden slajd`);
        setVideoSplitInfo(null);
      } else {
        // Video is too long — show split config
        const suggestedSegment = STORY_MAX_DURATION_SECONDS; // 60s
        const segments = Math.ceil(duration / suggestedSegment);
        setVideoSplitInfo({
          videoUrl: publicUrl,
          totalDuration: duration,
          segmentDuration: suggestedSegment,
          fileName: file.name,
          originalFile: file,
        });
        toast.success(
          `Film ${formatDuration(duration)} — proponuję ${segments} slajdów po ${suggestedSegment}s. Dostosuj i potwierdź podział.`
        );
      }
    } catch (error) {
      console.error('Error in video auto-split:', error);
      toast.dismiss();
      toast.error(error instanceof Error ? error.message : 'Nie udało się przygotować wideo');
    } finally {
      setIsVideoSplitting(false);
      e.target.value = '';
    }
  };

  // Step 2: User adjusts segment duration and confirms
  const handleSetVideoSegmentDuration = (seconds: number) => {
    if (!videoSplitInfo) return;
    const clamped = Math.max(5, Math.min(seconds, STORY_MAX_DURATION_SECONDS));
    setVideoSplitInfo(prev => prev ? { ...prev, segmentDuration: clamped } : null);
  };

  const handleConfirmVideoSplit = async () => {
    if (!videoSplitInfo) return;

    const { totalDuration, segmentDuration, originalFile } = videoSplitInfo;
    const segmentCount = Math.ceil(totalDuration / segmentDuration);
    const capped = Math.min(segmentCount, STORY_MAX_SLIDES);

    // Build segment definitions
    const segments: Array<{ startSeconds: number; endSeconds: number }> = [];
    for (let i = 0; i < capped; i++) {
      segments.push({
        startSeconds: i * segmentDuration,
        endSeconds: Math.min((i + 1) * segmentDuration, totalDuration),
      });
    }

    // Trim each segment using FFmpeg WASM and upload separately
    setIsVideoSplitting(true);
    const loadingToast = toast.loading('Przygotowywanie FFmpeg...');

    try {
      const trimResults = await trimAllSegments(
        originalFile,
        segments,
        (progress: TrimProgress) => {
          toast.loading(progress.message, { id: loadingToast });
        }
      );

      // Upload each trimmed segment as a separate file
      const newParts: StoryPartData[] = [];
      for (let i = 0; i < trimResults.length; i++) {
        const seg = segments[i];
        const trimResult = trimResults[i];

        toast.loading(`Przesyłanie segmentu ${i + 1}/${trimResults.length}...`, { id: loadingToast });

        // Convert Blob to File for uploadMediaToStorage
        const trimmedFile = new File(
          [trimResult.blob],
          `story_segment_${i + 1}.mp4`,
          { type: 'video/mp4' }
        );

        const { publicUrl, error: uploadError } = await uploadMediaToStorage(trimmedFile, { maxSizeMB: 100 });
        if (uploadError || !publicUrl) {
          throw new Error(`Nie udało się przesłać segmentu ${i + 1}: ${uploadError?.message || 'Brak URL'}`);
        }

        newParts.push({
          partOrder: i + 1,
          content: `Część ${i + 1}/${capped} (${formatDuration(seg.startSeconds)} – ${formatDuration(seg.endSeconds)})`,
          mediaUrl: publicUrl,
          mediaType: 'video',
          durationSeconds: trimResult.durationSeconds,
        });
      }

      setStoryParts(newParts);
      setVideoSplitInfo(null);
      toast.dismiss(loadingToast);

      if (segmentCount > STORY_MAX_SLIDES) {
        toast.success(
          `Wideo przycięte i podzielone na ${capped} slajdów (max ${STORY_MAX_SLIDES}).`
        );
      } else {
        toast.success(`Wideo przycięte i podzielone na ${capped} slajdów ✂️`);
      }
    } catch (error) {
      console.error('Error trimming/uploading segments:', error);
      toast.dismiss(loadingToast);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Nie udało się przyciąć wideo. Spróbuj ponownie.'
      );
    } finally {
      setIsVideoSplitting(false);
    }
  };

  const handleCancelVideoSplit = () => {
    setVideoSplitInfo(null);
  };

  // Helper: format seconds as mm:ss
  const formatDuration = (totalSec: number): string => {
    const m = Math.floor(totalSec / 60);
    const s = Math.round(totalSec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // ─── Schedule Handlers ────────────────────────────────────────────
  const handleDateChangeForReelsStory = (date: Date) => setSelectedDate(date);
  const handleTimeChangeForReelsStory = (time: string) => setSelectedTime(time);
  const handleTimezoneChangeForReelsStory = (timezone: string) => {
    setSelectedTimezone(timezone);
    setSelectedTime(getCurrentTimeInTimezone());
    setMinTime(getMinTime());
  };
  const handleCycleChangeForReelsStory = (newCycle: string) => setCycle(newCycle);
  const handleDaysChangeForReelsStory = (days: string[]) => setSelectedDays(days);
  const handleWebhookChangeForReelsStory = (url: string) => setCustomWebhook(url);
  const handleWebhookInfoToggleForReelsStory = () => setShowWebhookInfo(!showWebhookInfo);
  const handleAIAssistantToggleForReelsStory = () => setShowAIAssistant(!showAIAssistant);
  const handleAIContentGeneratedForReelsStory = (generatedContent: string) => {
    // For Story upload mode: try to parse AI response into slides
    if (contentType === 'story' && storyMode === 'upload') {
      const parsed = parseAIStoryResponse(generatedContent);

      // Check if there are existing slides with media that should be preserved
      const slidesWithMedia = storyParts.filter(p => !!p.mediaUrl);

      if (parsed && parsed.length > 0 && slidesWithMedia.length > 0) {
        // ─── MERGE MODE: preserve existing media, add AI-generated text ───
        const merged: StoryPartData[] = [];

        for (let i = 0; i < Math.max(storyParts.length, parsed.length); i++) {
          const existingSlide = storyParts[i];
          const aiSlide = parsed[i];

          if (existingSlide && aiSlide) {
            // Both exist: merge AI text into existing slide, keep media
            merged.push({
              ...existingSlide,
              content: aiSlide.content,
              template: existingSlide.mediaUrl ? (aiSlide.template || existingSlide.template) : aiSlide.template,
              // Preserve media-related fields
              mediaUrl: existingSlide.mediaUrl,
              mediaType: existingSlide.mediaType,
              startSeconds: existingSlide.startSeconds,
              endSeconds: existingSlide.endSeconds,
              // Merge template-specific fields from AI
              ctaUrl: aiSlide.ctaUrl || existingSlide.ctaUrl,
              ctaButtonText: aiSlide.ctaButtonText || existingSlide.ctaButtonText,
              quoteAuthor: aiSlide.quoteAuthor || existingSlide.quoteAuthor,
              pollOptionA: aiSlide.pollOptionA || existingSlide.pollOptionA,
              pollOptionB: aiSlide.pollOptionB || existingSlide.pollOptionB,
              partOrder: i + 1,
            });
          } else if (existingSlide && !aiSlide) {
            // More existing slides than AI generated — keep as-is
            merged.push({ ...existingSlide, partOrder: i + 1 });
          } else if (!existingSlide && aiSlide) {
            // More AI slides than existing — add as text-only
            merged.push({ ...aiSlide, partOrder: i + 1 });
          }
        }

        setStoryParts(merged);
        setContent(''); // Clear main textarea
        toast.success(`AI wygenerował tekst do ${parsed.length} slajdów — media zachowane! 🎨`);
        return;
      }

      if (parsed && parsed.length > 1) {
        // No existing media — create new slides from scratch (original behavior)
        setStoryParts(parsed);
        setContent(''); // Clear main textarea — content is now in slides
        toast.success(`AI stworzył ${parsed.length} slajdów! 🎨`);
        return;
      }
    }
    // Fallback: set as plain content (for posts, reels, or non-parseable responses)
    setContent(generatedContent);
  };
  const handleAiGeneratedToggleForReelsStory = () => setIsAiGenerated(!isAiGenerated);

  // ─── Reference Images & Logo Handlers ─────────────────────────────
  const handleReferenceImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (referenceImages.length >= 3) {
      toast.error('Maksymalnie 3 zdjęcia referencyjne');
      return;
    }
    try {
      setIsUploading(true);
      const { publicUrl, error } = await uploadImageToStorage(file);
      if (error) throw error;
      if (!publicUrl) throw new Error('Failed to get public URL');
      setReferenceImages(prev => [...prev, publicUrl]);
      toast.success('Zdjęcie referencyjne dodane');
    } catch (error) {
      console.error('Error uploading reference image:', error);
      toast.error('Nie udało się przesłać zdjęcia');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleReferenceImageRemove = async (index: number) => {
    try {
      setIsUploading(true);
      const url = referenceImages[index];
      if (url) await deleteImageFromStorage(url);
      setReferenceImages(prev => prev.filter((_, i) => i !== index));
      toast.success('Zdjęcie usunięte');
    } catch (error) {
      console.error('Error removing reference image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      const { publicUrl, error } = await uploadImageToStorage(file);
      if (error) throw error;
      if (!publicUrl) throw new Error('Failed to get public URL');
      setLogoUrl(publicUrl);
      toast.success('Logo dodane');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Nie udało się przesłać logo');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleLogoRemove = async () => {
    try {
      setIsUploading(true);
      if (logoUrl) await deleteImageFromStorage(logoUrl);
      setLogoUrl(null);
      toast.success('Logo usunięte');
    } catch (error) {
      console.error('Error removing logo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Validation ───────────────────────────────────────────────────
  const validateReelsStoryForm = (): boolean => {
    if (!selectedAccount) {
      toast.error('Wybierz konto do publikacji');
      return false;
    }

    if (contentType === 'reel') {
      if (reelMode === 'upload') {
        if (!videoUrl) {
          toast.error('Reel wymaga wideo — prześlij plik wideo');
          return false;
        }
      } else {
        // AI generate mode — need content/prompt
        if (!content.trim()) {
          toast.error('Podaj instrukcje dla AI do wygenerowania wideo');
          return false;
        }
      }
      if (content.length > REELS_MAX_CAPTION_LENGTH) {
        toast.error(`Opis Reel może mieć max ${REELS_MAX_CAPTION_LENGTH} znaków`);
        return false;
      }
    }

    if (contentType === 'story') {
      if (storyMode === 'ai_generate') {
        // AI generate mode — need content/prompt
        if (!content.trim()) {
          toast.error('Podaj instrukcje dla AI do wygenerowania wideo Story');
          return false;
        }
      } else {
        if (storyParts.length === 0) {
          toast.error('Historia wymaga co najmniej jednego slajdu');
          return false;
        }
        const hasContent = storyParts.some(p => p.content.trim() || p.mediaUrl);
        if (!hasContent) {
          toast.error('Co najmniej jeden slajd musi zawierać tekst lub media');
          return false;
        }
      }
    }

    return true;
  };

  // ─── Submit ───────────────────────────────────────────────────────
  const handleSubmitReelsStoryTask = async () => {
    if (!validateReelsStoryForm()) return;

    const { date: correctedDate, time: correctedTime } = autoCorrectDateTimeForReelsStory();

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const isDirectPublish = 
        (contentType === 'reel' && reelMode === 'upload' && !!videoUrl) ||
        (contentType === 'story' && storyMode === 'upload');
      const isStoryAiMode = contentType === 'story' && storyMode === 'ai_generate';

      const webhookUrl = isDirectPublish ? null : (customWebhook || import.meta.env.VITE_DEFAULT_WEBHOOK);
      if (!isDirectPublish && !webhookUrl) throw new Error('No webhook URL configured');

      const scheduledDate = new Date(`${format(correctedDate, 'yyyy-MM-dd')}T${correctedTime}`);
      const scheduledInTimezone = formatInTimeZone(
        scheduledDate,
        selectedTimezone,
        "yyyy-MM-dd'T'HH:mm:ssXXX"
      );

      const taskData = {
        content: content || (contentType === 'story' ? storyParts.map(p => p.content).filter(Boolean).join(' | ') : ''),
        content_type: contentType,
        platform: selectedPlatform,
        account_id: selectedAccount,
        scheduled_for: scheduledInTimezone,
        webhook_url: isDirectPublish ? null : webhookUrl,
        cycle,
        cycle_days: cycle === 'custom' ? selectedDays : null,
        video_url: contentType === 'reel' && reelMode === 'upload' ? videoUrl
          : contentType === 'story' && storyParts.length > 0 ? (storyParts.find(p => p.mediaUrl && p.mediaType === 'video')?.mediaUrl || null)
          : null,
        cover_image_url: contentType === 'reel' ? coverImageUrl : null,
        image_url: contentType === 'reel' ? coverImageUrl : null,
        generate_image: false,
        modify_image: false,
        full_modify_image: false,
        metadata: {
          is_ai_generated: isAiGenerated,
          content_type: contentType,
          direct_publish: isDirectPublish,
          reel_mode: contentType === 'reel' ? reelMode : undefined,
          story_mode: contentType === 'story' ? storyMode : undefined,
          ...(contentType === 'story' && storyMode === 'upload' ? { story_parts_count: storyParts.length } : {}),
          ...(contentType === 'reel' ? {
            has_cover_image: !!coverImageUrl,
            ...(reelCtaUrl ? { cta_url: reelCtaUrl } : {}),
            ...(reelCtaButtonText ? { cta_button_text: reelCtaButtonText } : {}),
          } : {}),
          ...((reelMode === 'ai_generate' || isStoryAiMode) ? {
            reference_images: referenceImages,
            logo_url: logoUrl,
          } : {}),
        },
        status: 'active',
        user_id: user.id,
        timezone: selectedTimezone,
      };

      let taskResult;
      if (editingTask) {
        taskResult = await supabase
          .from('scheduled_tasks')
          .update(taskData)
          .eq('id', editingTask.id)
          .eq('user_id', user.id)
          .select()
          .single();
      } else {
        taskResult = await supabase
          .from('scheduled_tasks')
          .insert([taskData])
          .select()
          .single();
      }

      if (taskResult.error) throw taskResult.error;
      const savedTask = taskResult.data;

      // Save story parts if content type is story (upload mode only)
      if (contentType === 'story' && storyMode === 'upload' && savedTask?.id) {
        // Delete existing parts if editing
        if (editingTask) {
          await supabase
            .from('story_parts')
            .delete()
            .eq('task_id', savedTask.id);
        }

        // Insert new parts
        if (storyParts.length > 0) {
          const partsToInsert = storyParts.map((part, i) => ({
            task_id: savedTask.id,
            part_order: i + 1,
            content: part.content,
            media_url: part.mediaUrl,
            media_type: part.mediaType,
            duration_seconds: part.durationSeconds,
            metadata: {
              ...(part.startSeconds !== undefined ? { start_seconds: part.startSeconds } : {}),
              ...(part.endSeconds !== undefined ? { end_seconds: part.endSeconds } : {}),
              ...(part.template ? { template: part.template } : {}),
              ...(part.ctaUrl ? { cta_url: part.ctaUrl } : {}),
              ...(part.ctaButtonText ? { cta_button_text: part.ctaButtonText } : {}),
              ...(part.quoteAuthor ? { quote_author: part.quoteAuthor } : {}),
              ...(part.pollOptionA ? { poll_option_a: part.pollOptionA } : {}),
              ...(part.pollOptionB ? { poll_option_b: part.pollOptionB } : {}),
            },
          }));

          const { error: partsError } = await supabase
            .from('story_parts')
            .insert(partsToInsert);

          if (partsError) {
            console.error('Error saving story parts:', partsError);
            toast.error('Task saved but story parts failed to save');
          }
        }
      }

      // For direct publish (Reels upload + Story upload) — create both posts + ready_posts entries immediately.
      // auto_publish_ready_posts does INNER JOIN posts↔ready_posts, so both must exist.
      // should_run_task() sees the ready_post and blocks process_task() from creating posts,
      // so we must create the posts row ourselves here.
      if (isDirectPublish && savedTask?.id) {
        if (contentType === 'story' && storyParts.length > 0) {
          // Stories: create one posts + ready_posts pair PER STORY PART
          // Each part has its own trimmed video/image URL
          for (let i = 0; i < storyParts.length; i++) {
            const part = storyParts[i];
            const partVideoUrl = part.mediaType === 'video' ? (part.mediaUrl || null) : null;
            const partImageUrl = part.mediaType === 'image' ? (part.mediaUrl || null) : null;

            // CTA is stored ONLY in metadata — DB publish functions handle it natively
            // (IG Stories use `link` param for link sticker, Reels append to caption)
            const partContent = part.content || `Część ${i + 1}/${storyParts.length}`;

            const { error: postError } = await supabase
              .from('posts')
              .insert([{
                task_id: savedTask.id,
                user_id: user.id,
                content: partContent,
                content_type: 'story',
                platform: selectedPlatform,
                status: 'ready',
                video_url: partVideoUrl,
                image_url: partImageUrl,
                metadata: {
                  is_in_progress: false,
                  stage: 'ready',
                  direct_publish: true,
                  content_type: 'story',
                  video_url: partVideoUrl,
                  story_part_index: i,
                  story_total_parts: storyParts.length,
                  platform: selectedPlatform,
                  creation_timestamp: new Date().toISOString(),
                  task_cycle: cycle,
                  task_timezone: selectedTimezone,
                  ...(part.template ? { template: part.template } : {}),
                  ...(part.ctaUrl ? { cta_url: part.ctaUrl } : {}),
                  ...(part.ctaButtonText ? { cta_button_text: part.ctaButtonText } : {}),
                },
              }]);

            if (postError) {
              console.error(`Error creating post for story part ${i + 1}:`, postError);
              toast.error(`Task saved but post creation failed for part ${i + 1}`);
            }

            // CTA is stored ONLY in metadata — same as posts above
            const readyPartContent = part.content || `Część ${i + 1}/${storyParts.length}`;

            const { error: readyPostError } = await supabase
              .from('ready_posts')
              .insert([{
                task_id: savedTask.id,
                user_id: user.id,
                content: readyPartContent,
                platform: selectedPlatform,
                is_default: true,
                status: 'ready',
                metadata: {
                  direct_publish: true,
                  content_type: 'story',
                  video_url: partVideoUrl,
                  story_part_index: i,
                  story_total_parts: storyParts.length,
                  creation_timestamp: new Date().toISOString(),
                  ...(part.template ? { template: part.template } : {}),
                  ...(part.ctaUrl ? { cta_url: part.ctaUrl } : {}),
                  ...(part.ctaButtonText ? { cta_button_text: part.ctaButtonText } : {}),
                },
              }]);

            if (readyPostError) {
              console.error(`Error creating ready post for story part ${i + 1}:`, readyPostError);
              toast.error(`Task saved but ready post creation failed for part ${i + 1}`);
            }
          }

          // ═══════════════════════════════════════════════════════════════
          // AUTO-GENERATE CTA CARD SLIDE
          // Instagram/Facebook Stories API does NOT support link stickers.
          // So we generate a professional CTA card image (Canvas → JPEG)
          // and publish it as an ADDITIONAL story slide.
          // ═══════════════════════════════════════════════════════════════
          const ctaPart = storyParts.find(p => p.ctaUrl && p.ctaUrl.trim() !== '');
          if (ctaPart && ctaPart.ctaUrl) {
            try {
              console.log('[CTA] Generating CTA card slide for story...');
              const ctaBlob = await generateCtaCardImage(
                ctaPart.ctaButtonText || 'Sprawdź teraz →',
                ctaPart.ctaUrl
              );

              // Upload CTA card image to Supabase Storage
              const ctaFileName = `story-cta/${user.id}/${Date.now()}_cta_card.jpg`;
              const { error: ctaUploadError } = await supabase.storage
                .from('media')
                .upload(ctaFileName, ctaBlob, {
                  contentType: 'image/jpeg',
                  upsert: false,
                });

              if (ctaUploadError) {
                console.error('[CTA] Upload failed:', ctaUploadError);
              } else {
                // Get public/signed URL for the CTA card
                const { data: ctaSignedData } = await supabase.storage
                  .from('media')
                  .createSignedUrl(ctaFileName, 86400); // 24 hours

                const ctaImageUrl = ctaSignedData?.signedUrl;
                if (ctaImageUrl) {
                  console.log('[CTA] Card uploaded, creating story slide...');
                  const ctaSlideIndex = storyParts.length;

                  // Create posts entry for CTA slide
                  const { error: ctaPostError } = await supabase
                    .from('posts')
                    .insert([{
                      task_id: savedTask.id,
                      user_id: user.id,
                      content: `${ctaPart.ctaButtonText || 'CTA'} — ${ctaPart.ctaUrl}`,
                      content_type: 'story',
                      platform: selectedPlatform,
                      status: 'ready',
                      video_url: null,
                      image_url: ctaImageUrl,
                      metadata: {
                        is_in_progress: false,
                        stage: 'ready',
                        direct_publish: true,
                        content_type: 'story',
                        is_cta_slide: true,
                        story_part_index: ctaSlideIndex,
                        story_total_parts: storyParts.length + 1,
                        platform: selectedPlatform,
                        creation_timestamp: new Date().toISOString(),
                        task_cycle: cycle,
                        task_timezone: selectedTimezone,
                        cta_url: ctaPart.ctaUrl,
                        cta_button_text: ctaPart.ctaButtonText || '',
                        cta_card_storage_path: ctaFileName,
                      },
                    }]);

                  if (ctaPostError) {
                    console.error('[CTA] Post creation failed:', ctaPostError);
                  }

                  // Create ready_posts entry for CTA slide
                  const { error: ctaReadyError } = await supabase
                    .from('ready_posts')
                    .insert([{
                      task_id: savedTask.id,
                      user_id: user.id,
                      content: `${ctaPart.ctaButtonText || 'CTA'} — ${ctaPart.ctaUrl}`,
                      platform: selectedPlatform,
                      image_url: ctaImageUrl,
                      is_default: true,
                      status: 'ready',
                      metadata: {
                        direct_publish: true,
                        content_type: 'story',
                        is_cta_slide: true,
                        story_part_index: ctaSlideIndex,
                        story_total_parts: storyParts.length + 1,
                        creation_timestamp: new Date().toISOString(),
                        cta_url: ctaPart.ctaUrl,
                        cta_button_text: ctaPart.ctaButtonText || '',
                      },
                    }]);

                  if (ctaReadyError) {
                    console.error('[CTA] Ready post creation failed:', ctaReadyError);
                  } else {
                    console.log('[CTA] ✅ CTA card slide created successfully!');
                  }
                }
              }
            } catch (ctaError) {
              console.error('[CTA] CTA card generation failed:', ctaError);
              // Don't fail the whole publish — CTA slide is optional
            }
          }
        } else {
          // Reels: single posts + ready_posts entry
          // CTA is stored ONLY in metadata — DB publish function handles caption enrichment
          const displayContent = content || '';

          const { error: postError } = await supabase
            .from('posts')
            .insert([{
              task_id: savedTask.id,
              user_id: user.id,
              content: displayContent,
              content_type: contentType,
              platform: selectedPlatform,
              status: 'ready',
              video_url: videoUrl,
              image_url: coverImageUrl,
              metadata: {
                is_in_progress: false,
                stage: 'ready',
                direct_publish: true,
                content_type: contentType,
                video_url: videoUrl,
                cover_image_url: coverImageUrl,
                platform: selectedPlatform,
                creation_timestamp: new Date().toISOString(),
                task_cycle: cycle,
                task_timezone: selectedTimezone,
                ...(reelCtaUrl ? { cta_url: reelCtaUrl } : {}),
                ...(reelCtaButtonText ? { cta_button_text: reelCtaButtonText } : {}),
              },
            }])
            .select()
            .single();

          if (postError) {
            console.error('Error creating post for direct publish:', postError);
            toast.error('Task saved but post creation failed');
          }

          const { error: readyPostError } = await supabase
            .from('ready_posts')
            .insert([{
              task_id: savedTask.id,
              user_id: user.id,
              content: displayContent,
              platform: selectedPlatform,
              image_url: coverImageUrl,
              is_default: true,
              status: 'ready',
              metadata: {
                direct_publish: true,
                content_type: contentType,
                video_url: videoUrl,
                cover_image_url: coverImageUrl,
                creation_timestamp: new Date().toISOString(),
                ...(reelCtaUrl ? { cta_url: reelCtaUrl } : {}),
                ...(reelCtaButtonText ? { cta_button_text: reelCtaButtonText } : {}),
              },
            }]);

          if (readyPostError) {
            console.error('Error creating ready post:', readyPostError);
            toast.error('Task saved but ready post creation failed');
          }
        }
      }

      const typeLabel = contentType === 'reel' ? 'Reel' : 'Story';
      toast.success(editingTask ? `${typeLabel} zaktualizowany!` : `${typeLabel} zaplanowany!`);
      await onSubmit();
      onClose();
    } catch (error) {
      console.error('Error scheduling reels/story task:', error);
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Test Webhook ─────────────────────────────────────────────────
  const handleTestReelsStoryWebhook = async () => {
    if (!validateReelsStoryForm()) return;

    setIsTesting(true);
    const loadingToast = toast.loading('Testing webhook...');

    try {
      const webhookUrl = customWebhook || import.meta.env.VITE_DEFAULT_WEBHOOK;
      if (!webhookUrl) throw new Error('No webhook URL configured');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const webhookData = {
        text: content,
        account_id: selectedAccount,
        platform: selectedPlatform,
        content_type: contentType,
        tool_id: editingTask?.id || 'test',
        usage_id: `test-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        is_test: true,
        test: true,
        webhookUrl,
        executionMode: 'test',
        ...(contentType === 'reel' ? {
          video_url: videoUrl,
          cover_image_url: coverImageUrl,
          ...(reelCtaUrl ? { cta_url: reelCtaUrl } : {}),
          ...(reelCtaButtonText ? { cta_button_text: reelCtaButtonText } : {}),
        } : {}),
        ...(contentType === 'story' ? {
          story_parts: storyParts.map(p => ({
            part_order: p.partOrder,
            content: p.content,
            media_url: p.mediaUrl,
            media_type: p.mediaType,
            duration_seconds: p.durationSeconds,
            ...(p.template ? { template: p.template } : {}),
            ...(p.startSeconds !== undefined ? { start_seconds: p.startSeconds } : {}),
            ...(p.endSeconds !== undefined ? { end_seconds: p.endSeconds } : {}),
            ...(p.ctaUrl ? { cta_url: p.ctaUrl } : {}),
            ...(p.ctaButtonText ? { cta_button_text: p.ctaButtonText } : {}),
            ...(p.quoteAuthor ? { quote_author: p.quoteAuthor } : {}),
            ...(p.pollOptionA ? { poll_option_a: p.pollOptionA } : {}),
            ...(p.pollOptionB ? { poll_option_b: p.pollOptionB } : {}),
          })),
          story_parts_count: storyParts.length,
        } : {}),
      };

      // Use Supabase edge function as CORS proxy to avoid browser CORS issues
      let response: Response;
      try {
        response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          mode: 'cors',
          body: JSON.stringify(webhookData),
        });
      } catch {
        // If direct fetch fails (CORS), route through Supabase edge function proxy
        console.warn('Direct webhook failed (likely CORS), routing via edge function proxy...');
        const { data: proxyData, error: proxyError } = await supabase.functions.invoke('webhook-proxy', {
          body: {
            url: webhookUrl,
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: webhookData,
          },
        });

        if (proxyError) throw new Error(`Webhook proxy failed: ${proxyError.message}`);
        
        // Wrap proxy response to match fetch Response format
        const proxyResult = proxyData;
        if (proxyResult?.error) throw new Error(proxyResult.error);

        // Create a synthetic successful result using proxy data
        const result = proxyResult?.data || proxyResult;
        const processedResult = Array.isArray(result) ? result[0] : result;

        if (!processedResult || !processedResult.data) {
          // If webhook doesn't return content data, create test post directly
          const { error: insertError } = await supabase
            .from('posts')
            .insert([{
              content: content,
              content_type: contentType,
              platform: selectedPlatform,
              published_at: new Date().toISOString(),
              image_url: coverImageUrl,
              video_url: contentType === 'reel' ? videoUrl : null,
              task_id: editingTask?.id,
              user_id: user.id,
              status: 'ready',
              metadata: {
                webhook_response: processedResult,
                is_test: true,
                stage: 'ready',
                content_type: contentType,
                is_in_progress: false,
                creation_timestamp: new Date().toISOString(),
                webhook_payload: webhookData,
                cors_proxy_used: true,
              },
            }]);

          if (insertError) throw new Error(`Failed to create test post: ${insertError.message}`);

          toast.dismiss(loadingToast);
          toast.success('Test completed successfully! (via proxy)');
          await onSubmit();
          onClose();
          return;
        }

        // Process normal result from proxy
        const { error: insertError } = await supabase
          .from('posts')
          .insert([{
            content: processedResult.data.content || content,
            content_type: contentType,
            platform: selectedPlatform,
            published_at: new Date().toISOString(),
            image_url: processedResult.data.image_url || coverImageUrl,
            video_url: contentType === 'reel' ? (processedResult.data.video_url || videoUrl) : null,
            task_id: editingTask?.id,
            user_id: user.id,
            status: 'ready',
            metadata: {
              webhook_response: processedResult,
              is_test: true,
              stage: 'ready',
              content_type: contentType,
              is_in_progress: false,
              creation_timestamp: new Date().toISOString(),
              webhook_payload: webhookData,
              cors_proxy_used: true,
            },
          }]);

        if (insertError) throw new Error(`Failed to create test post: ${insertError.message}`);

        toast.dismiss(loadingToast);
        toast.success('Test completed successfully! (via proxy)');
        await onSubmit();
        onClose();
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Webhook failed with status ${response.status}: ${errorText}`);
      }

      const rawResult = await response.json();
      const result = Array.isArray(rawResult) ? rawResult[0] : rawResult;

      if (!result || !result.data) throw new Error('Invalid webhook response format');

      const { error: insertError } = await supabase
        .from('posts')
        .insert([{
          content: result.data.content || content,
          content_type: contentType,
          platform: selectedPlatform,
          published_at: new Date().toISOString(),
          image_url: result.data.image_url || coverImageUrl,
          video_url: contentType === 'reel' ? (result.data.video_url || videoUrl) : null,
          task_id: editingTask?.id,
          user_id: user.id,
          status: 'ready',
          metadata: {
            webhook_response: result,
            is_test: true,
            stage: 'ready',
            content_type: contentType,
            is_in_progress: false,
            creation_timestamp: new Date().toISOString(),
            webhook_payload: webhookData,
          },
        }]);

      if (insertError) throw new Error(`Failed to create test post: ${insertError.message}`);

      toast.dismiss(loadingToast);
      toast.success('Test completed successfully!');
      await onSubmit();
      onClose();
    } catch (error) {
      console.error('Test webhook error:', error);
      toast.dismiss(loadingToast);
      toast.error(error instanceof Error ? error.message : 'Webhook test failed');
    } finally {
      setIsTesting(false);
    }
  };

  return {
    // Content type
    contentType,
    handleContentTypeSelectForReelsStory,
    // Core
    content,
    setContent,
    selectedPlatform,
    selectedAccount,
    selectedDate,
    selectedTime,
    selectedTimezone,
    userDefaultTimezone,
    cycle,
    selectedDays,
    customWebhook,
    connectedAccounts,
    isLoading,
    isTesting,
    isUploading,
    minTime,
    showWebhookInfo,
    showAIAssistant,
    isAiGenerated,
    // Reels
    reelMode,
    setReelMode,
    videoUrl,
    coverImageUrl,
    referenceImages,
    logoUrl,
    reelCtaUrl,
    setReelCtaUrl,
    reelCtaButtonText,
    setReelCtaButtonText,
    handleReelsVideoChange,
    handleReelsVideoRemove,
    handleReelsCoverImageChange,
    handleReelsCoverImageRemove,
    handleReferenceImageUpload,
    handleReferenceImageRemove,
    handleLogoUpload,
    handleLogoRemove,
    // Stories
    storyMode,
    setStoryMode,
    storyParts,
    handleAddStoryPart,
    handleRemoveStoryPart,
    handleUpdateStoryPart,
    handleMoveStoryPart,
    handleAutoSplitContentForStory,
    handleStoryPartMediaChange,
    handleVideoAutoSplit,
    isVideoSplitting,
    videoSplitInfo,
    handleSetVideoSegmentDuration,
    handleConfirmVideoSplit,
    handleCancelVideoSplit,
    // Handlers
    handlePlatformSelectForReelsStory,
    handleAccountSelectForReelsStory,
    handleDateChangeForReelsStory,
    handleTimeChangeForReelsStory,
    handleTimezoneChangeForReelsStory,
    handleCycleChangeForReelsStory,
    handleDaysChangeForReelsStory,
    handleWebhookChangeForReelsStory,
    handleWebhookInfoToggleForReelsStory,
    handleAIAssistantToggleForReelsStory,
    handleAIContentGeneratedForReelsStory,
    handleAiGeneratedToggleForReelsStory,
    // Actions
    handleSubmitReelsStoryTask,
    handleTestReelsStoryWebhook,
  };
}
